// First-login intake, shown once to an invited employee before they reach the
// workspace.
//
// It exists for two reasons. The obvious one is collecting the details HR needs
// without chasing people. The load-bearing one is the password: a Supabase
// invite link fires SIGNED_IN, not PASSWORD_RECOVERY, so the invitee arrives
// with a session but NO password ever set — and signing in requires
// verifyPassword() to succeed before the code is even emailed (auth.tsx:205).
// Without this screen, every invited account would work exactly once and then
// lock its owner out for good.
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '../components/primitives';
import { InlineStatus } from '../components/feedback';
import {
  AuthCard,
  AuthNote,
  AuthShell,
  Field,
  MIN_PASSWORD,
  PwToggle,
  passwordProblem,
  type PasswordProblem,
} from '../components/auth-ui';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { todayIso } from '../lib/workdays';

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' } as const;

// A named group of fields, under a rule. The two section titles were unlabelled
// <div>s in the same uppercase recipe as the field labels beneath them, so the
// form read as one run of eleven inputs; the title is a heading now, and does
// not look like a label.
const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}) => {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={id}
      style={{
        ...stack,
        marginTop: 'var(--hf-space-5)',
        paddingTop: 'var(--hf-space-5)',
        borderTop: '1px solid var(--hf-rule)',
      }}
    >
      <div>
        <h2
          id={id}
          style={{
            margin: 0,
            fontFamily: 'var(--hf-font-display)',
            fontSize: 'var(--hf-text-lg)',
            fontWeight: 'var(--hf-weight-medium)',
            letterSpacing: '-0.01em',
            color: 'var(--hf-ink)',
          }}
        >
          {title}
        </h2>
        <AuthNote style={{ marginTop: 'var(--hf-space-1)', fontSize: 'var(--hf-text-sm)' }}>
          {note}
        </AuthNote>
      </div>
      {children}
    </div>
  );
};

export function WelcomeScreen() {
  const { session, profile, signOut } = useAuth();

  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [mobile, setMobile] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [dob, setDob] = useState('');
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState(profile?.location ?? '');
  const [emName, setEmName] = useState('');
  const [emRel, setEmRel] = useState('');
  const [emPhone, setEmPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  // A password complaint belongs to its field; a failed save belongs to the form.
  const [error, setError] = useState<PasswordProblem | { on: 'form'; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!supabase || !session) {
      setError({ on: 'form', text: 'Not connected.' });
      return;
    }
    // Everything else is enforced natively by the browser (required, type=email,
    // type=date max=today). These are the only two rules HTML cannot express.
    const problem = passwordProblem(password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    const uid = session.user.id;

    // 1. Roster fields. profiles_update_self covers id = auth.uid(), and neither
    //    column is guarded by protect_profile_columns.
    const { error: pErr } = await supabase
      .from('profiles')
      .update({ full_name: fullName.trim(), location: location.trim() || null })
      .eq('id', uid);

    // 2. The private half. contact_rw (FOR ALL, profile_id = auth.uid()) covers
    //    it; the row may not exist yet, hence upsert. Empty values are stripped
    //    so the write is purely additive and can never blank out something HR
    //    already filled in.
    const patch: Record<string, string> = {
      mobile: mobile.trim(),
      personal_email: personalEmail.trim(),
      address: address.trim(),
      dob,
      emergency_name: emName.trim(),
      emergency_relation: emRel.trim(),
      emergency_phone: emPhone.trim(),
    };
    const { error: cErr } = await supabase.from('employee_contact').upsert(
      {
        profile_id: uid,
        ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v)),
      },
      { onConflict: 'profile_id' },
    );

    const first = pErr ?? cErr;
    if (first) {
      setBusy(false);
      setError({ on: 'form', text: `Couldn’t save — ${first.message}` });
      return;
    }

    // 3. LAST: the password, plus the flag that drops the gate. Doing this last
    //    means a failure in 1 or 2 leaves the user on the form with everything
    //    still typed, rather than through the gate with nothing saved.
    //
    //    same_password: they took the "Forgot password" rescue after the invite
    //    link expired and already set this password on ResetPasswordScreen.
    //    GoTrue refuses to set it again, and without this retry they would be
    //    stuck on this form permanently.
    let { error: uErr } = await supabase.auth.updateUser({
      password,
      data: { onboard: false },
    });
    if (uErr?.code === 'same_password') {
      ({ error: uErr } = await supabase.auth.updateUser({ data: { onboard: false } }));
    }
    setBusy(false);
    if (uErr) {
      setError({ on: 'form', text: uErr.message });
      return;
    }
    // No reload() — updateUser fires USER_UPDATED, which re-runs loadFor through
    // the [session] effect and re-evaluates the gate (auth.tsx:153,161).
  };

  const errorOn = (on: 'password' | 'confirm') => (error?.on === on ? error.text : undefined);

  return (
    <AuthShell>
      <AuthCard
        title="Welcome to Hyprflow"
        sub="A few details to finish setting up your account. Your contact details are visible only to you and HR."
        onSubmit={submit}
      >
        {/* Not shown: it tells a password manager which account the new
            password belongs to, so it saves the pair. */}
        <input
          type="email"
          autoComplete="username"
          value={session?.user.email ?? ''}
          readOnly
          hidden
        />
        <div style={stack}>
          <Field
            label="Full name"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
            required
            autoFocus
          />
          <Field
            label="Mobile"
            type="tel"
            value={mobile}
            onChange={setMobile}
            placeholder="+91 90000 00000"
            autoComplete="tel"
            required
          />
          <Field
            label="Personal email"
            type="email"
            value={personalEmail}
            onChange={setPersonalEmail}
            placeholder="you@gmail.com"
            autoComplete="email"
            required
          />
          {/* Nothing said which of these eleven fields could be left empty. */}
          <Field
            label="Date of birth (optional)"
            type="date"
            value={dob}
            onChange={setDob}
            max={todayIso()}
            autoComplete="bday"
          />
          <Field
            label="Address (optional)"
            value={address}
            onChange={setAddress}
            autoComplete="street-address"
          />
          <Field
            label="City (optional)"
            value={location}
            onChange={setLocation}
            placeholder="Kannur, IN"
            autoComplete="address-level2"
          />
        </div>

        {/* autoComplete off: a browser otherwise offers the person's own name
            and number for someone else's. */}
        <Section title="In case of emergency" note="Optional. Who HR should call.">
          <Field label="Contact name" value={emName} onChange={setEmName} autoComplete="off" />
          <Field
            label="Relationship"
            value={emRel}
            onChange={setEmRel}
            placeholder="Parent"
            autoComplete="off"
          />
          <Field
            label="Contact number"
            type="tel"
            value={emPhone}
            onChange={setEmPhone}
            autoComplete="off"
          />
        </Section>

        <Section
          title="Set a password"
          note="You’ll need this every time you sign in, alongside an emailed code."
        >
          <Field
            label="Password"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            placeholder={`At least ${MIN_PASSWORD} characters`}
            autoComplete="new-password"
            required
            error={errorOn('password')}
            trailing={<PwToggle shown={showPw} onClick={() => setShowPw((s) => !s)} />}
          />
          <Field
            label="Confirm password"
            type={showPw ? 'text' : 'password'}
            value={confirm}
            onChange={setConfirm}
            placeholder="The same password again"
            autoComplete="new-password"
            required
            error={errorOn('confirm')}
          />
        </Section>

        {error?.on === 'form' && (
          <div style={{ marginTop: 'var(--hf-space-4)' }}>
            <InlineStatus tone="error">{error.text}</InlineStatus>
          </div>
        )}

        <Button
          variant="primary"
          size="xl"
          full
          type="submit"
          loading={busy}
          iconRight="arrowRight"
          style={{ marginTop: 'var(--hf-space-5)' }}
        >
          {busy ? 'Saving…' : 'Finish setup'}
        </Button>
      </AuthCard>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--hf-space-4)' }}>
        <Button variant="ghost" size="sm" onClick={signOut}>
          Sign out
        </Button>
      </div>
    </AuthShell>
  );
}
